/*
 * ErrorBoundary — catches render-time crashes and shows a recovery UI
 * instead of a blank white screen.
 *
 * ! file components/ErrorBoundary.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("[Weavian] Uncaught render error:", error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-card">
            <div className="error-boundary-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="#EF4444" strokeWidth="2.5" opacity="0.2" />
                <circle cx="24" cy="24" r="22" stroke="#EF4444" strokeWidth="2.5"
                  strokeDasharray="6 4" />
                <path d="M24 14v12" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="24" cy="32" r="1.5" fill="#EF4444" />
              </svg>
            </div>
            <h2 className="error-boundary-title">Something went wrong</h2>
            <p className="error-boundary-msg">
              An unexpected error occurred in the UI. This won't affect your
              edge nodes — they continue operating independently.
            </p>
            {this.state.error && (
              <pre className="error-boundary-detail">
                {this.state.error.toString()}
              </pre>
            )}
            <div className="error-boundary-actions">
              <button className="error-boundary-retry" onClick={this.handleReload}>
                Try Again
              </button>
              <button
                className="error-boundary-reload"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
